import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'
import moment from 'moment'

import { Button } from '../../ui/button'
import { Label } from '../../ui/label'
import { Input } from '../../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import { Checkbox } from '../../ui/checkbox'
import FacilityMultiSelect from '../shared/FacilityMultiSelect'

const DATE_PRESETS = [
  { value: '30', label: 'Last 30 days' },
  { value: '60', label: 'Last 60 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 180 days' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'custom', label: 'Custom Date Range' }
]

function calculateDatesFromPreset(preset) {
  const today = moment()

  switch (preset) {
    case '30':
      return {
        date_from: today.clone().subtract(30, 'days').format('YYYY-MM-DD'),
        date_to: today.format('YYYY-MM-DD')
      }
    case '60':
      return {
        date_from: today.clone().subtract(60, 'days').format('YYYY-MM-DD'),
        date_to: today.format('YYYY-MM-DD')
      }
    case '90':
      return {
        date_from: today.clone().subtract(90, 'days').format('YYYY-MM-DD'),
        date_to: today.format('YYYY-MM-DD')
      }
    case '180':
      return {
        date_from: today.clone().subtract(180, 'days').format('YYYY-MM-DD'),
        date_to: today.format('YYYY-MM-DD')
      }
    case 'ytd':
      return {
        date_from: today.clone().startOf('year').format('YYYY-MM-DD'),
        date_to: today.format('YYYY-MM-DD')
      }
    default:
      return null
  }
}

function FinancialTransactionsForm({
  form,
  facilities,
  onSubmit,
  isSubmitting,
  isFacilityScoped = false,
  demo = false
}) {
  const { control, handleSubmit, register, setValue, watch } = form
  const [datePreset, setDatePreset] = useState('30')

  const dateFrom = watch('date_from')
  const dateTo = watch('date_to')

  useEffect(() => {
    if (datePreset !== 'custom') {
      const dates = calculateDatesFromPreset(datePreset)
      if (dates) {
        setValue('date_from', dates.date_from)
        setValue('date_to', dates.date_to)
      }
    }
  }, [datePreset, setValue])

  function handlePresetChange(value) {
    setDatePreset(value)
  }

  function handleFormSubmit(data) {
    const submissionData = {
      ...data,
      date_from: moment(data.date_from).unix(),
      date_to: moment(data.date_to).endOf('day').unix()
    }
    onSubmit(submissionData)
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="date_preset">Date Range</Label>
          <Select value={datePreset} onValueChange={handlePresetChange}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select date range" />
            </SelectTrigger>
            <SelectContent>
              {DATE_PRESETS.map((preset) => (
                <SelectItem key={preset.value} value={preset.value}>
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {datePreset !== 'custom' && dateFrom && dateTo && (
            <p className="text-sm text-gray-500 mt-1">
              {moment(dateFrom).format('MMM D, YYYY')} - {moment(dateTo).format('MMM D, YYYY')}
            </p>
          )}
        </div>

        {datePreset === 'custom' && (
          <>
            <div>
              <Label htmlFor="date_from">From Date</Label>
              <Input id="date_from" type="date" {...register('date_from')} className="mt-1" />
            </div>

            <div>
              <Label htmlFor="date_to">To Date</Label>
              <Input id="date_to" type="date" {...register('date_to')} className="mt-1" />
            </div>
          </>
        )}

        {!isFacilityScoped && (
          <div>
            <Label>Facilities</Label>
            <Controller
              name="facility_ids"
              control={control}
              render={({ field }) => (
                <FacilityMultiSelect facilities={facilities} value={field.value} onChange={field.onChange} />
              )}
            />
            <p className="text-sm text-gray-500 mt-1">Leave empty for all facilities</p>
          </div>
        )}

        <div>
          <Label htmlFor="file_format">File Format</Label>
          <Controller
            name="file_format"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="xls">Excel (XLS)</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div hidden={demo}>
          <Label>Delivery Method</Label>
          <Controller
            name="delivery_method"
            control={control}
            render={({ field }) => (
              <div className="space-y-2 mt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="email"
                    checked={field.value === 'email' || field.value === 'both'}
                    disabled={field.value === 'email'}
                    onCheckedChange={(checked) => {
                      if (checked && field.value === 'download') {
                        field.onChange('both')
                      } else if (checked) {
                        field.onChange('email')
                      } else if (field.value === 'both') {
                        field.onChange('download')
                      }
                    }}
                  />
                  <Label htmlFor="email" className="font-normal cursor-pointer">
                    Email me when ready
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="download"
                    checked={field.value === 'download' || field.value === 'both'}
                    disabled={field.value === 'download'}
                    onCheckedChange={(checked) => {
                      if (checked && field.value === 'email') {
                        field.onChange('both')
                      } else if (checked) {
                        field.onChange('download')
                      } else if (field.value === 'both') {
                        field.onChange('email')
                      }
                    }}
                  />
                  <Label htmlFor="download" className="font-normal cursor-pointer">
                    Available for download
                  </Label>
                </div>
              </div>
            )}
          />
        </div>

        <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-sm text-amber-800">
            Stripe fees shown are estimates (2.9% + $0.30 per transaction). Actual fees may vary based on card type and
            other factors.
          </p>
        </div>
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? 'Generating Preview...' : 'Preview Report'}
      </Button>
    </form>
  )
}

FinancialTransactionsForm.propTypes = {
  demo: PropTypes.bool,
  form: PropTypes.object.isRequired,
  facilities: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isFacilityScoped: PropTypes.bool
}

export default FinancialTransactionsForm
